import { PPTEditorStateEnumType, PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { observable, reaction, IReactionDisposer } from 'mobx';
import { IUserPriviledgeService, IEditorStateCallback, IStateOptions, Scope } from './user-priviledge-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import ReportManager from './report-manager';
import { IReportManager } from './report-manager/report-manager.interface';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';

const PV_DELAY_TIME = 2000;  // PV延时2s上报
/**
 * 用户编辑权限相关状态服务
 * 使用文档 https://docs.qq.com/doc/DQW5vcGZBQWNKSFBL
 */
export default class UserPriviledgeService implements IUserPriviledgeService {
    /**
     * 当前用户是否超过了最高协作数量，如果超过，则锁定只读
     */
    public isOverCoorperateNum = false;

    // 文档权限（文档自身的真实权限）
    public fileAuth: PPTEditorStateEnumType;


    // 取消订阅
    public disposeReaction: IReactionDisposer;

    // 是否锁住编辑器状态， true：不允许再修改编辑器状态， false：允许修改
    public isLockEditorState = false;

    // 回调函数列表
    private callbacks: Array<(state: PPTEditorStateEnumType) => void> = [];

    private willChangeCallbacks: Array<IEditorStateCallback> = [];

    // 是否是历史版本
    private isHistoricalVersion = false;

    private reportManager: IReportManager;

    // 编辑器权限
    @observable
    private pptEditorStateInner =  PPTEditorStateType.EDITABLE;

    public get pptEditorState() {
        return this.pptEditorStateInner;
    }

    public set pptEditorState(value) {
        throw new Error('forbid direct modification, you can use the "setPPTEditorState" api');
    }

    public constructor(private selectionService: ISelectionService, private uiDataService: IUiDataService) {
        this.reportManager = new ReportManager();
        // 权限变化，需要刷新
        this.registerPrivilegeChange();

        reaction(() => this.pptEditorState, (newState) => {
            this.uiDataService.presentation.pptEditorState = newState;
        });

        this.registerCallbacks();
    }

    public isEditable(): boolean {
        return this.pptEditorStateInner <= PPTEditorStateType.EDITABLE;
    }

    // 更新历史版本状态
    public updateHistoricalVersionStatus(isHistoricalVersion: boolean, canUpdateEditorState: boolean) {
        this.isHistoricalVersion = isHistoricalVersion;
        if (!canUpdateEditorState) return;
        if (isHistoricalVersion) {
            // 进入历史版本后切换为只读权限
            this.pptEditorStateInner = PPTEditorStateType.COPYABLE;
        } else {
            this.pptEditorStateInner = PPTEditorStateType.EDITABLE;
        }
    }
    // 获取历史版本状态
    public getHistoricalVersionStatus() {
        return this.isHistoricalVersion;
    }

    /**
     *  设置PPTEditorState
     * @param state PPTEditorStateEnumType
     */
    public setPPTEditorState(stateOptions: IStateOptions): void {
        const { state, scope = Scope.ALL } = stateOptions;
        // 每次设置状态前，先判断所有业务方是否同意修改
        const result = this.willChangeCallbacks.every((callback) => {
            const isAllowChangeState = callback(state);
            return isAllowChangeState;
        });
        // 有业务方不同意修改状态，则直接return；
        if (!result) return;

        // 走到此处，所有业务方都同意修改状态
        this.pptEditorStateInner = state;

        switch (scope) {
            case Scope.ALL:
                this.setAllSlideModelState(state);
                break;
            case Scope.CURRENT:
                this.setCurrentSlideModelState(state);
                break;
            case Scope.SERVICE:
                break;
        }
    }

    /**
     * 监听PPTEditorStateChange
     * @param callback 监听函数
     * @returns Function 移除监听函数
     */
    public onPPTEditorStateChange(callback: (state: PPTEditorStateEnumType) => void): Function {
        this.callbacks.push(callback);
        return () => {
            const index = this.callbacks.indexOf(callback);
            if (index === -1) return;
            this.callbacks.splice(index, 1);
        };
    }

    // 添加编辑器权限willChange时刻的回调，用于每次修改状态前，先判断所有业务方是否同意修改，并返回一个反注册的函数
    public addStateWillChangeCallbacks(callback: IEditorStateCallback): Function {
        this.willChangeCallbacks.push(callback);
        return () => {
            const index =  this.willChangeCallbacks.indexOf(callback);
            if (index === -1) return;
            this.willChangeCallbacks.splice(index, 1);
        };
    }

    /**
     * 注册回调
     */
    private registerCallbacks(): void {
        this.disposeReaction = reaction(
            () => this.pptEditorState,
            (pptEditorState) => {
                this.callbacks.forEach((callback) => {
                    callback(pptEditorState);
                });
            },
        );
    }

    /**
     * 监听文档权限变更
     */
    private registerPrivilegeChange() {
        // 根据文档权限设置编辑区的编辑状态
        onSlideAppReady(() => {
            const { copyable, editable } = (SlideDataServices.PrivilegeManager() ?? {});
            const currentSlide = this.selectionService?.currentSlide;
            if (editable) {
                this.pptEditorStateInner = PPTEditorStateType.EDITABLE;
                if (currentSlide) {
                    currentSlide.pptEditorState = PPTEditorStateType.EDITABLE;
                }
            } else if (copyable) {
                this.pptEditorStateInner = PPTEditorStateType.COPYABLE;
                if (currentSlide) {
                    currentSlide.pptEditorState = PPTEditorStateType.COPYABLE;
                }
            } else {
                this.pptEditorStateInner = PPTEditorStateType.SELECTABLE;
                if (currentSlide) {
                    currentSlide.pptEditorState = PPTEditorStateType.SELECTABLE;
                }
            }
            /**
             * 如果当前没有 currentSlide，添加监听并给 currentSlide 设置默认值
             * 之前逻辑是在渲染的时候给 currentSlide 设置权限，不太合理，@qiyuan 做查看 sdk 时把渲染时修改 pptEditorState 的逻辑去掉了
             * 所以在这里加上设置初始值的逻辑
             */
            if (!currentSlide) {
                const currentSlideReaction = reaction(() => this.selectionService?.currentSlide, (currentSlide) => {
                    if (currentSlide) {
                        // 设置完初始值后就销毁 reaction
                        currentSlideReaction();
                        currentSlide.pptEditorState = this.pptEditorStateInner;
                    }
                });
            }
            setTimeout(() => {
                this.reportManager.reportVisit();
            }, PV_DELAY_TIME);

            this.fileAuth = this.pptEditorStateInner;
        });
    }

    /**
     *  设置所有slideModel的pptEditorState
     * @param state PPTEditorStateEnumType
     */
    private setAllSlideModelState(state: PPTEditorStateEnumType): void {
        const slideList = this.selectionService.slides;
        // 需要重置每一页的PPTEditorState，否则切页时元素可编辑
        slideList?.forEach((slideModel) => {
            slideModel.pptEditorState = state;
        });
    }

    /**
         * 设置当前页slideModel的pptEditorState
         * @param state PPTEditorStateEnumType
         */
    private setCurrentSlideModelState(state: PPTEditorStateEnumType): void {
        const { currentSlide } = this.selectionService;
        if (!currentSlide) return;
        currentSlide.pptEditorState = state;
    }
}
